import { useState } from 'react';
import { Link } from 'react-router';
import { DataTable, ErrorState, Spinner } from '../../components/ui.jsx';
import { LeaseStatus } from '../../components/lease.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf } from '../../lib/format.js';

const TABS = [
  ['open', 'Live and pending'],
  ['awaiting_signatures', 'Waiting to be signed'],
  ['active', 'In force'],
  ['draft', 'Drafts'],
  ['ended', 'Finished'],
  ['any', 'All']
];

const LeaseList = () => {
  useTitle('Leases');
  const [tab, setTab] = useState('open');
  const { data, loading, error, reload } = useFetch(`/tenancies?status=${tab}`, [tab]);

  const items = data?.items || [];

  const columns = [
    {
      key: 'what',
      header: 'Property and tenant',
      render: (lease) => (
        <div className="cell-stack">
          <Link to={`/landlord/leases/${lease.id}`} className="cell-title">
            {lease.unit?.label ? `${lease.unit.label}, ` : ''}
            {lease.property?.title}
          </Link>
          <span className="muted">{lease.tenant?.name}</span>
          <span className="muted">{lease.tenant?.phone}</span>
        </div>
      )
    },
    {
      key: 'term',
      header: 'Term',
      render: (lease) => (
        <div className="cell-stack">
          <span>From {formatDate(lease.startDate)}</span>
          <span className="muted">{lease.endDate ? `To ${formatDate(lease.endDate)}` : 'Month to month'}</span>
        </div>
      )
    },
    {
      key: 'rent',
      header: 'Rent',
      render: (lease) => (
        <div className="cell-stack">
          <strong>{formatXaf(lease.rent)}</strong>
          <span className="muted">due on day {lease.rentDueDay}</span>
        </div>
      )
    },
    {
      key: 'balance',
      header: 'Account',
      render: (lease) =>
        lease.totals ? (
          <div className="cell-stack">
            {lease.totals.balance > 0 ? (
              <strong className="owing">{formatXaf(lease.totals.balance)} owing</strong>
            ) : lease.totals.balance < 0 ? (
              <span>{formatXaf(-lease.totals.balance)} in credit</span>
            ) : (
              <span className="muted">Clear</span>
            )}
            {lease.totals.overdue > 0 ? (
              <span className="overdue-flag">{formatXaf(lease.totals.overdue)} overdue</span>
            ) : null}
          </div>
        ) : (
          <span className="muted">-</span>
        )
    },
    {
      key: 'status',
      header: 'Status',
      render: (lease) => (
        <div className="cell-stack">
          <LeaseStatus status={lease.status} />
          {lease.status === 'awaiting_signatures' ? (
            <span className="muted">
              {lease.signatures?.landlord ? 'You signed' : 'You have not signed'}
              {lease.signatures?.tenant ? ', tenant signed' : ', tenant has not'}
            </span>
          ) : null}
        </div>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (lease) => (
        <div className="cell-actions">
          <Link to={`/landlord/leases/${lease.id}`} className="btn btn-outline btn-sm">
            Open
          </Link>
          <Link to={`/leases/${lease.id}/print`} className="btn btn-ghost btn-sm">
            Print
          </Link>
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Leases</h1>
          <p className="muted">
            Every agreement you have drawn up, what it is worth, and whether the rent on it is up to date.
          </p>
        </div>
        <div className="row-actions">
          <Link to="/landlord/tenants" className="btn btn-ghost">
            My tenants
          </Link>
          <Link to="/landlord/leases/new" className="btn btn-primary">
            Draw up a lease
          </Link>
        </div>
      </header>

      <div className="tab-row">
        {TABS.map(([value, label]) => (
          <button key={value} type="button" className={`tab ${tab === value ? 'is-current' : ''}`} onClick={() => setTab(value)}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <DataTable
          columns={columns}
          rows={items}
          empty={{
            title: tab === 'open' ? 'No live leases' : 'Nothing in this group',
            message:
              tab === 'open'
                ? 'Draw up a lease for somebody already renting from you, or for an applicant you have approved.'
                : 'Try another tab.'
          }}
        />
      ) : null}
    </div>
  );
};

export default LeaseList;
